/**
 * Vuex store module for real-time chat messaging.
 */
import { getMessages, sendMessage } from '@/services/message.service'

const state = {
  conversations: [],
  activeChatMessages: [],
  typingUsers: {},     
  chatUnreadTotal: 0,
  _currentUserId: null,
  _activePartnerId: null,

  // Centralized Messaging States
  localMessages: {},      // { [convoId]: [ optimisticMessages ] }
  messageOffset: 0,
  hasMoreMessages: true,
  loadingMessages: false,
  loadingMore: false
}

const getters = {
  getChatConversations: state => state.conversations,
  getActiveChatMessages: state => state.activeChatMessages,
  getChatTyping: state => state.typingUsers,
  getActivePartnerId: state => state._activePartnerId,
  getLoadingMessages: state => state.loadingMessages,
  getLoadingMore: state => state.loadingMore,
  getHasMoreMessages: state => state.hasMoreMessages,
  
  getChatUnreadTotal: state => {
    return state.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
  },

  // THE ULTIMATE UNIFIER: Merges real database logs with temporary optimistic items
  getCombinedMessages: state => {
    const convoId = state._activePartnerId
    if (!convoId) return []

    const storeMsgs = state.activeChatMessages || []
    const locals = state.localMessages[convoId] || []
    const combined = [...storeMsgs, ...locals]
    
    const uniqueMessages = []
    const seenMessageIds = new Set()
    
    combined.forEach(m => {
      const msgKey = m.id || m.tempId || `${m.sender_id}_${m.created_at}_${m.content || m.message}`
      if (!seenMessageIds.has(msgKey)) {
        seenMessageIds.add(msgKey)
        uniqueMessages.push(m)
      }
    })
    
    return uniqueMessages
  }
}

const mutations = {
  setChatConversations(state, conversations) {
    state.conversations = conversations || []
  },

  setActiveChatMessages(state, messages) {
    // console.log("set active chat messages", messages)
    state.activeChatMessages = messages || []
    this.commit('pruneLocalMessages', state._activePartnerId)
  },

  prependChatMessages(state, olderMessages) {
    if (olderMessages && olderMessages.length > 0) {
      state.activeChatMessages = [...olderMessages, ...state.activeChatMessages]
    }
    this.commit('pruneLocalMessages', state._activePartnerId)
  },

  addChatMessage(state, message) {
    const exists = state.activeChatMessages.find(m => m.id === message.id)
    if (!exists) {
      state.activeChatMessages.push(message)
    }
    // Automatically scrub matching optimistic layout templates
    this.commit('pruneLocalMessages', message.sender_id === state._currentUserId ? message.recipient_id : message.sender_id)
  },

  receiveChatMessage(state, message) {
    // Normalize IDs — socket payloads and Vuex user id can differ by type (string vs number).
    // Fall back to root user id for tabs that never opened Messages (and thus never set _currentUserId).
    const currentUserIdRaw = state._currentUserId ?? this.getters?.getUserId ?? this.state?.user?.id
    if (currentUserIdRaw != null && state._currentUserId == null) {
      state._currentUserId = currentUserIdRaw
    }
    const currentUserId = currentUserIdRaw != null ? String(currentUserIdRaw) : null
    const senderId = message.sender_id != null ? String(message.sender_id) : null
    const recipientId = message.recipient_id != null ? String(message.recipient_id) : null
    const activePartnerId = state._activePartnerId != null ? String(state._activePartnerId) : null
    const isOwnMessage = !!currentUserId && senderId === currentUserId
    const partnerId = isOwnMessage ? recipientId : senderId

    if (partnerId && partnerId === activePartnerId) {
      const exists = state.activeChatMessages.find(m => m.id === message.id)
      if (!exists) {
        state.activeChatMessages.push(message)
      }
      this.commit('pruneLocalMessages', partnerId)
    }

    // Update conversations layout hierarchy...
    const idx = state.conversations.findIndex(c => String(c.id) === String(partnerId))
    const shouldIncrementUnread = !isOwnMessage && partnerId !== activePartnerId
    if (idx !== -1) {
      const convo = { ...state.conversations[idx] }
      convo.last_message = message.content
      convo.last_message_at = message.created_at
      convo.last_sender_id = message.sender_id
      if (shouldIncrementUnread) {
        convo.unread_count = (convo.unread_count || 0) + 1
      }
      state.conversations.splice(idx, 1)
      state.conversations.unshift(convo)
    } else {
      state.conversations.unshift({
        id: partnerId,
        name: null,
        firstname: null,
        lastname: null,
        email: null,
        last_message: message.content,
        last_message_at: message.created_at,
        last_sender_id: message.sender_id,
        unread_count: shouldIncrementUnread ? 1 : 0
      })
    }
  },

  // ─── OPTIMISTIC SYNC MUTATIONS ───────────────────────────────────
  addOptimisticMessage(state, { convoId, message }) {
    // console.log("add optimistic message", message)
    // debugger
    if (!state.localMessages[convoId]) {
      state.localMessages[convoId] = []
    }
    state.localMessages[convoId].push(message)
    state.localMessages = { ...state.localMessages }
  },

  updateOptimisticMessageStatus(state, { convoId, tempId, status }) {
    // console.log("update optimistic message", tempId)
    const locals = state.localMessages[convoId] || []
    const index = locals.findIndex(m => m.tempId === tempId)
    // console.log(locals[index])
    // debugger
    if (index !== -1) {
      locals[index] = { ...locals[index], status }
      state.localMessages[convoId] = [...locals]
    }
  },

  pruneLocalMessages(state, convoId) {
    if (!convoId || !state.localMessages[convoId]) return
    const myStoreContents = new Set(
      state.activeChatMessages.filter(m => m.sender_id === state._currentUserId).map(m => m.content || m.message)
    )
    state.localMessages[convoId] = state.localMessages[convoId].filter(lm => {
      if (lm.status === 'failed') return true
      return !myStoreContents.has(lm.content)
    })
  },

  // Pagination mutations
  setPaginationFlags(state, { offset, hasMore, loadingMain, loadingMore }) {
    if (offset !== undefined) state.messageOffset = offset
    if (hasMore !== undefined) state.hasMoreMessages = hasMore
    if (loadingMain !== undefined) state.loadingMessages = loadingMain
    if (loadingMore !== undefined) state.loadingMore = loadingMore
  },

  setChatCurrentUserId(state, userId) {
    state._currentUserId = userId
  },
  setActivePartnerId(state, partnerId) {
    state._activePartnerId = partnerId
  },
  setChatTyping(state, { sender_id, timestamp, is_typing }) {
    state.typingUsers = {
      ...state.typingUsers,
      [sender_id]: is_typing ? (timestamp || Date.now()) : 0
    }
    // Auto-clear after 3 seconds
    setTimeout(() => {
      if (state.typingUsers[sender_id] === (timestamp || 0)) {
        const copy = { ...state.typingUsers }
        delete copy[sender_id]
        state.typingUsers = copy
      }
    }, 3000)
  },
  clearChatTyping(state, userId) {
    const copy = { ...state.typingUsers }
    delete copy[userId]
    state.typingUsers = copy
  },
  markConversationRead(state, partnerId) {
    const idx = state.conversations.findIndex(c => c.id === partnerId)
    if (idx !== -1) {
      state.conversations[idx] = { ...state.conversations[idx], unread_count: 0 }
    }
  },
  /**
   * Updates all sent messages in the active chat thread to 'read_at = new Date()'
   * when the partner sends a read receipt.
   */
  markActiveMessagesAsRead(state, partnerId) {
    console.log("mark active messages as read", partnerId, state.activeChatMessages)
    if (state.activeChatMessages && state.activeChatMessages.length > 0) {
      const now = new Date()
      state.activeChatMessages.forEach(msg => {
        // If the message was sent to this partner and is currently unread, mark it read
        if (msg.recipient_id === partnerId && !msg.read_at) {
          msg.read_at = now
        }
      })
    }
  }
}

const actions = {
  /**
   * Centralized History Request Core
   */
  async loadChatMessagesFromAPI({ commit, state }, { convoId, isLoadMore = false, userId = null }) {
    if (convoId === 'system') return

    // Resolve user context from parameter fallback or current store state
    const activeUid = userId || state._currentUserId
    if (!activeUid) {
      console.warn('[Vuex Action] Postponing chat message fetch: No valid user ID available yet.')
      return
    }

    if (isLoadMore) {
      if (state.loadingMore || !state.hasMoreMessages) return
      commit('setPaginationFlags', { loadingMore: true })
    } else {
      commit('setPaginationFlags', { offset: 0, hasMore: true, loadingMain: true })
    }

    try {
      const response = await getMessages(activeUid, convoId, 50, state.messageOffset)
      
      if (response?.status === 200 && response.data) {
        const messages = response.data
        const hasMore = messages.length === 50
        const sorted = [...messages].reverse()
        
        if (isLoadMore) {
          commit('prependChatMessages', sorted)
        } else {
          commit('setActiveChatMessages', sorted)
        }
        
        commit('setPaginationFlags', { 
          offset: state.messageOffset + messages.length, 
          hasMore: hasMore 
        })
      }
    } catch (e) {
      console.error('[Vuex Action] Error pulling conversational logs:', e)
    } finally {
      commit('setPaginationFlags', { loadingMain: false, loadingMore: false })
    }
  },

  /**
   * Centralized Outbound Pipeline (Handles creation, sending, errors & retries)
   */
  async executeCentralizedSend({ commit, state }, { convoId, content, targetTempId = null }) {
    let tempId = targetTempId

    // console.log({ convoId, content, targetTempId })
    
    if (targetTempId) {
      commit('updateOptimisticMessageStatus', { convoId, tempId, status: 'sending' })
    } else {
      tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
      const targetItem = {
        tempId,
        sender_id: state._currentUserId,
        recipient_id: convoId,
        content: content,
        created_at: new Date().toISOString(),
        status: 'sending'
      }
      commit('addOptimisticMessage', { convoId, message: targetItem })
    }

    try {
      await sendMessage(state._currentUserId, convoId, content)
      commit('updateOptimisticMessageStatus', { convoId, tempId, status: 'sent' })
    } catch (e) {
      console.error('[Vuex Action] Failed outbound delivery chain:', e)
      commit('updateOptimisticMessageStatus', { convoId, tempId, status: 'failed' })
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}